import {MUIDataTableColumnDef, MUIDataTableOptions, MUISortOptions} from "mui-datatables";

/**
 * A/B 要件で使用する localStorage キー定義
 * A：メイン画面共通保持項目
 * B：画面個別保持項目
 * - shared: 画面横断で同期する値
 * - page.settings: 画面ごとに保持する値
 * - columns.*: View Columns 用
 */
export const APOLLO_LOCAL_STORAGE_KEYS = {
  SHARED: "apollo.shared.v1",
  PAGE_SETTINGS: "apollo.page.settings.v1",
  COLUMNS_COMMON: "apollo.columns.common.v1",
  COLUMNS_SHARED_PREFIX: "apollo.columns.shared.",
  COLUMNS_PAGE_SPECIFIC_PREFIX: "apollo.columns.pageSpecific.",
  COLUMNS_VERSION_SUFFIX: ".v1",
} as const;

// ページ別設定を保存するときに使う画面 ID 定義
export const APOLLO_PAGE_ID = {
  HOME: "home",
  CAMPAIGN: "campaign",
  ADGROUP_ZEUS: "adgroupZeus",
  ADGROUP_MS: "adgroupMs",
  AD_ZEUS: "adZeus",
  AD_MS: "adMs",
  ACCOUNT_GROUP_LIST: "accountGroupList",
  UNAPPROVED_ACCOUNT_GROUP_LIST: "unapprovedAccountGroupList",
  ACCOUNT_GROUP_ACCOUNTS: "accountGroupAccounts",
  ROLE_LIST: "roleList",
  ACCOUNT_GROUP_USERS: "accountGroupUsers",
  USER_LIST: "userList",
  AD_CLIENT_ICON_REVIEW_LIST: "adClientIconReviewList",
  AD_CLIENT_ICON_APPROVED_REVIEW_LIST: "adClientIconApprovedReviewList",
  AD_CLIENT_ICON_LIST: "adClientIconList",
  AD_EDIT_MS: "adEditMs",
  CUSTOM_SEGMENT_LIST: "customSegmentList",
  BUDGET: "budget",
  AUDIENCE_LIST: "audienceList",
  EVENT_LIST: "eventList",
  SET_POINT_LIST: "setPointList",
  OPERATION_HISTORY: "operationHistory",
  MANUAL_LIST: "manualList",
  BILLING_RESOURCE_MASTER: "billingResourceMaster",
  INDUSTRY_MASTER: "industryMaster",
  PRODUCT_MASTER: "productMaster",
  NOTIFY_MESSAGE_LIST: "notifyMessageList",
  PAST_REPORT_LIST: "pastReportList",
  REPORT_DETAIL: "reportDetail",
  REPORT_LIST: "reportList",
  AFFINITY_CATEGORY_LIST: "affinityCategoryList",
  CUSTOM_SEGMENT_ACCOUNT_LINK: "customSegmentAccountLink",
  REVIEW_RESULT_DOWNLOAD_LIST: "reviewResultDownloadList",
  AD_ASSET_LIST_ZEUS: "adAssetListZeus",
  AD_ASSET_LIST_MS: "adAssetListMs",
  ACCOUNT_SELECT_HEADER_MENU: "accountSelectHeaderMenu",
  FIXED_BILLING_AMOUNT_LIST: "fixedBillingAmountList",
} as const;

export type ApolloPageId = typeof APOLLO_PAGE_ID[keyof typeof APOLLO_PAGE_ID];

// 列 ID ごとの表示状態を保持するための Map
type ColumnValueMap = {[key: string]: boolean};
// フィルタ ID ごとの選択値を保持するための Map。null は「選択なし」を表す。
type SharedFilters = {[key: string]: string[] | null};

// A：メイン画面共通保持項目 要件で画面横断共有する設定
type ApolloSharedSettings = {
  calendar?: {
    startDate?: string | null;
    endDate?: string | null;
  };
  pageSize?: number;
  filters?: SharedFilters;
};

// 固定列 / 画面固有列の localStorage 保存形式
type ApolloColumnStorage = {
  columns: ColumnValueMap;
  filters?: SharedFilters;
};

// CV ポイントなど動的に生成される列の localStorage 保存形式
type ApolloDynamicColumnStorage = {
  dynamicColumns: ColumnValueMap;
};

// MUIDataTable のソート状態をページ単位で保存する形式
type ApolloSortState = {
  name?: string;
  direction?: string;
};

// B：画面個別保持項目 要件でページ単位に保持する状態
type ApolloPageState = {
  jumpToPage?: number;
  sort?: ApolloSortState;
  filters?: SharedFilters;
};

// B：画面個別保持項目 要件の localStorage ルート保存形式
type ApolloPageSettingsStorage = {
  pageSize?: {[pageId: string]: number};
  pages?: {[pageId: string]: ApolloPageState};
};

// 列設定を適用 / 保存するときに必要なページ情報
export type ApolloSharedColumnOptions = {
  pageId: ApolloPageId;
  accountGroupId?: number | string | null;
  data?: any[];
};

// DataTable options 生成時に使う列設定込みのパラメータ
export type ApolloSharedTableOptionsParams = ApolloSharedColumnOptions & {
  columns: MUIDataTableColumnDef[];
  defaultPageSize?: number;
};

// 既存 MUIDataTable options に localStorage キーを追加した型
type ApolloTableOptionsWithStorage = MUIDataTableOptions & {storageKey?: string};

// ページサイズ未保存時の初期値
export const DEFAULT_PAGE_SIZE = 10;
// ページサイズを画面横断で共有する対象画面
const SHARED_PAGE_SIZE_TARGETS = new Set<ApolloPageId>([
  APOLLO_PAGE_ID.HOME,
  APOLLO_PAGE_ID.CAMPAIGN,
  APOLLO_PAGE_ID.ADGROUP_ZEUS,
  APOLLO_PAGE_ID.ADGROUP_MS,
  APOLLO_PAGE_ID.AD_ZEUS,
  APOLLO_PAGE_ID.AD_MS,
]);

// A：メイン画面共通保持項目 要件で画面横断共有する列の表示初期値
const COMMON_COLUMN_DEFAULTS: ColumnValueMap = {
  campaignType: true,
  transactionType: true,
  campaignName: true,
  billingType: true,
  adGroupId: true,
  adGroupName: true,
  osType: true,
  targeting: true,
  adId: true,
  adName: true,
  adType: true,
  reviewStatus: true,
  adAssetInfo: true,
  status: true,
  cost: true,
  imp: true,
  vimp: false,
  click: true,
  ctr: true,
  vctr: false,
  cpc: true,
  cpm: true,
  cvTotal: false,
  lastClickCvTotal: true,
  assistCvTotal: true,
  cvrTotal: false,
  lastClickCvrTotal: true,
  assistCvrTotal: false,
  cpaTotal: false,
  lastClickCpaTotal: true,
  assistCpaTotal: false,
  optCv: true,
  optCvr: true,
  optCpa: true,
  trigger: true,
};

// A：メイン画面共通保持項目 要件で画面横断共有するフィルタの初期値
const SHARED_FILTER_DEFAULTS: SharedFilters = {
  campaignName: null,
  campaignType: null,
  status: null,
  displayParentArchive: null,
  transactionType: null,
  billingType: null,
  adGroupName: null,
  adGroupId: null,
  osType: null,
  targeting: null,
  adType: null,
  cost: null,
  imp: null,
  click: null,
  ctr: null,
  cpc: null,
  cpm: null,
  cvTotal: null,
  lastClickCvTotal: null,
  assistCvTotal: null,
  cvrTotal: null,
  lastClickCvrTotal: null,
  assistCvrTotal: null,
  cpaTotal: null,
  lastClickCpaTotal: null,
  assistCpaTotal: null,
  adId: null,
  adName: null,
};

// PAGE_COLUMN_DEFAULT_OVERRIDES 列定義から初期表示状態を判断できない画面固有列の例外初期値
const PAGE_COLUMN_DEFAULT_OVERRIDES: {[key in ApolloPageId]?: ColumnValueMap} = {
  [APOLLO_PAGE_ID.HOME]: {
    accountGroupId: false,
  },
};

// 画面ごとに異なる物理列名を、保存用の共通論理 ID に寄せるための Map
const FIXED_COLUMN_ID_MAP: {[key: string]: string} = {
  platformName: "menu",
  platformCampaignName: "campaignName",
  adCampaignId: "campaignName",
  adCampaignName: "campaignName",
  campaignGoal: "campaignObjective",
  chargeType: "billingType",
  pmpType: "transactionType",
  pmpTypeLabel: "transactionType",
  adGroupStatus: "status",
  adGroupName: "adGroupName",
  bidCtgry: "billingType",
  osTargetingsLabel: "osType",
  targetInfo: "targeting",
  "targetInfo.targetType": "targeting",
  targetingType: "targeting",
  creativeStatus: "status",
  creativeName: "adName",
  adName: "adName",
  reviewStatusForView: "reviewStatus",
  images: "adAssetInfo",
  assetImageUrl: "adAssetInfo",
  trackingPointCvTotal: "cvTotal",
  trackingPointCvClickThrough: "lastClickCvTotal",
  trackingPointCvAssist: "assistCvTotal",
  trackingPointCvrTotal: "cvrTotal",
  trackingPointCvrClickThrough: "lastClickCvrTotal",
  trackingPointCvrAssist: "assistCvrTotal",
  trackingPointCpaTotal: "cpaTotal",
  trackingPointCpaClickThrough: "lastClickCpaTotal",
  trackingPointCpaAssist: "assistCpaTotal",
  lastCvTotal: "optCv",
  lastCvrTotal: "optCvr",
  lastCpaTotal: "optCpa",
};

// 画面別に上書きが必要な固定列 ID Map
const PAGE_FIXED_COLUMN_ID_MAP: {[key in ApolloPageId]?: {[key: string]: string}} = {
  [APOLLO_PAGE_ID.HOME]: {
    id: "accountId",
    accountgroupId: "accountGroupId",
    accountgroupName: "accountGroupName",
  },
  [APOLLO_PAGE_ID.CAMPAIGN]: {
    id: "campaignId",
  },
  [APOLLO_PAGE_ID.ADGROUP_ZEUS]: {
    id: "adGroupId",
    name: "adGroupName",
  },
  [APOLLO_PAGE_ID.ADGROUP_MS]: {
    id: "adGroupId",
    cpcLimit: "bidCtgry",
  },
  [APOLLO_PAGE_ID.AD_ZEUS]: {
    id: "adId",
    name: "adName",
  },
  [APOLLO_PAGE_ID.AD_MS]: {
    id: "adId",
    creativeId: "adId",
  },
};

// 動的指標列の物理名を保存用の短い論理 ID に変換する Map
const DYNAMIC_METRIC_ID_MAP: {[key: string]: string} = {
  trackingPointCvClickThrough: "lastClickCv",
  trackingPointCvAssist: "assistCv",
  trackingPointCvrClickThrough: "lastClickCvr",
  trackingPointCvrAssist: "assistCvr",
  trackingPointCpaClickThrough: "lastClickCpa",
};

// ブラウザ環境で localStorage が利用できるかを判定する
const isBrowserStorageAvailable = () => typeof window !== "undefined" && !!window.localStorage;

// JSON 形式の localStorage 値を読み込み、未保存や壊れた JSON の場合は fallback を返す
const readJson = <T,>(key: string, fallback: T): T => {
  if (!isBrowserStorageAvailable()) return fallback;
  const raw = window.localStorage.getItem(key);
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
};

// JSON 形式で localStorage に値を書き込む
const writeJson = (key: string, value: unknown) => {
  if (!isBrowserStorageAvailable()) return;
  window.localStorage.setItem(key, JSON.stringify(value));
};

// View Columns の保存先キー生成
const pageSpecificKey = (pageId: ApolloPageId) =>
  `${APOLLO_LOCAL_STORAGE_KEYS.COLUMNS_PAGE_SPECIFIC_PREFIX}${pageId}${APOLLO_LOCAL_STORAGE_KEYS.COLUMNS_VERSION_SUFFIX}`;

// アカウントグループ別に共有する動的列の保存先キーを生成する
const sharedDynamicKey = (accountGroupId?: number | string | null) =>
  `${APOLLO_LOCAL_STORAGE_KEYS.COLUMNS_SHARED_PREFIX}${accountGroupId ?? "default"}${APOLLO_LOCAL_STORAGE_KEYS.COLUMNS_VERSION_SUFFIX}`;

// MUIDataTable の display 値を boolean に正規化する
const normalizeDisplay = (display: unknown, defaultValue: boolean): boolean => {
  if (display === "excluded") return false;
  if (display === "true") return true;
  if (display === "false") return false;
  if (typeof display === "boolean") return display;
  return defaultValue;
};

// 許可された列 ID だけを localStorage 保存対象として残す
const filterColumns = (columns: ColumnValueMap, allowedIds: string[]) => {
  const allowed = new Set(allowedIds);
  return Object.keys(columns).reduce<ColumnValueMap>((acc, key) => {
    if (allowed.has(key)) acc[key] = columns[key];
    return acc;
  }, {});
};

// A：メイン画面共通保持項目 要件の共有設定を localStorage から読み込む
const readSharedSettings = (): ApolloSharedSettings => {
  const stored = readJson<ApolloSharedSettings>(APOLLO_LOCAL_STORAGE_KEYS.SHARED, {});
  return stored;
};

// A：メイン画面共通保持項目 要件の共有設定を localStorage に保存する
const writeSharedSettings = (settings: ApolloSharedSettings) => {
  writeJson(APOLLO_LOCAL_STORAGE_KEYS.SHARED, settings);
};

/**
 * B：画面個別保持項目 要件用のページ単位設定を読み出す
 * 1キー配下で pageSize / page / sort / filters をまとめて管理する。
 */
const readPageSettings = (): ApolloPageSettingsStorage =>
  readJson<ApolloPageSettingsStorage>(APOLLO_LOCAL_STORAGE_KEYS.PAGE_SETTINGS, {});

// B：画面個別保持項目 要件のページ単位設定を localStorage に保存する
const writePageSettings = (settings: ApolloPageSettingsStorage) => {
  writeJson(APOLLO_LOCAL_STORAGE_KEYS.PAGE_SETTINGS, settings);
};

// 指定ページの B：画面個別保持項目 要件状態を取得する
const readPageState = (pageId: ApolloPageId): ApolloPageState => readPageSettings().pages?.[pageId] ?? {};

// 指定ページの B：画面個別保持項目 要件状態を既存値とマージして保存する
const savePageState = (pageId: ApolloPageId, pageState: ApolloPageState) => {
  const pageSettings = readPageSettings();
  writePageSettings({
    ...pageSettings,
    pages: {
      ...(pageSettings.pages ?? {}),
      [pageId]: {
        ...(pageSettings.pages?.[pageId] ?? {}),
        ...pageState,
      },
    },
  });
};

// 指定ページのページサイズを B：画面個別保持項目 要件側に保存する
const savePageSizeSetting = (pageId: ApolloPageId, pageSize: number) => {
  const pageSettings = readPageSettings();
  writePageSettings({
    ...pageSettings,
    pageSize: {
      ...(pageSettings.pageSize ?? {}),
      [pageId]: pageSize,
    },
  });
};

// 指定ページのページサイズを取得し、未保存なら fallback を返す
const getPageSizeSetting = (pageId: ApolloPageId, fallback: number) => {
  const pageSize = readPageSettings().pageSize?.[pageId];
  return typeof pageSize === "number" && pageSize > 0 ? pageSize : fallback;
};

// getApolloPageSizeSetting ページ単位で保持されたページサイズを取得、見つからなければ fallback を返却
export const getApolloPageSizeSetting = (pageId: ApolloPageId, fallback = DEFAULT_PAGE_SIZE) =>
  getPageSizeSetting(pageId, fallback);

// saveApolloPageSizeSetting ページ単位のページサイズをローカルストレージへ保存
export const saveApolloPageSizeSetting = (pageId: ApolloPageId, pageSize: number) => {
  savePageSizeSetting(pageId, pageSize);
};

// createApolloPageSizeTableOptions rowsPerPage / JumpToPage の復元と変更時保存を MUIDataTable options に付与
export const createApolloPageSizeTableOptions = <T extends {
  rowsPerPage?: number;
  page?: number;
  onChangeRowsPerPage?: (numberOfRows: number) => void;
  onChangePage?: (currentPage: number) => void;
}>(
  options: T,
  pageId: ApolloPageId,
  fallback = DEFAULT_PAGE_SIZE,
): T => {
  const rowsPerPage = getPageSizeSetting(pageId, options.rowsPerPage ?? fallback);
  const pageState = readPageState(pageId);
  const page = typeof pageState.jumpToPage === "number" ? pageState.jumpToPage : options.page;
  return {
    ...options,
    rowsPerPage,
    page,
    onChangeRowsPerPage: (numberOfRows: number) => {
      savePageSizeSetting(pageId, numberOfRows);
      options.onChangeRowsPerPage?.(numberOfRows);
    },
    onChangePage: (currentPage: number) => {
      savePageState(pageId, {jumpToPage: currentPage});
      options.onChangePage?.(currentPage);
    },
  };
};

// getApolloSharedCalendar 画面横断で保持されたカレンダー期間を取得、見つからなければ fallback を返却
export const getApolloSharedCalendar = (fallback: {startDate: string; endDate: string}) => {
  const shared = readSharedSettings();
  return {
    startDate: shared.calendar?.startDate || fallback.startDate,
    endDate: shared.calendar?.endDate || fallback.endDate,
  };
};

// saveApolloSharedCalendar 画面横断で共有するカレンダー期間をローカルストレージへ保存
export const saveApolloSharedCalendar = (startDate: string, endDate: string) => {
  const shared = readSharedSettings();
  writeSharedSettings({
    ...shared,
    calendar: {
      startDate,
      endDate,
    },
  });
};

// getApolloSharedPageSize 画面横断で保持されたページサイズを取得、見つからなければ fallback を返却
export const getApolloSharedPageSize = (fallback = DEFAULT_PAGE_SIZE) => {
  const pageSize = readSharedSettings().pageSize;
  return typeof pageSize === "number" && pageSize > 0 ? pageSize : fallback;
};

// saveApolloSharedPageSize 画面横断で共有するページサイズをローカルストレージへ保存
export const saveApolloSharedPageSize = (pageSize: number) => {
  const shared = readSharedSettings();
  writeSharedSettings({
    ...shared,
    pageSize,
  });
};

// getApolloSharedFilterList フィルタ ID をもとにローカルストレージに保持された選択値を取得、見つからなければ fallback を返却
export const getApolloSharedFilterList = (filterId: string, fallback: string[] = []) => {
  const filters = readSharedSettings().filters;
  if (!filters || !Object.prototype.hasOwnProperty.call(filters, filterId)) return fallback;
  const value = filters[filterId];
  if (!Array.isArray(value)) return fallback;
  if (filterId !== "displayParentArchive") return value;

  return getDisplayParentArchiveVisibleValues(value, fallback);
};

// saveApolloSharedFilters 複数の共有フィルタを既存値とマージして保存
const saveApolloSharedFilters = (filters: SharedFilters) => {
  const shared = readSharedSettings();
  const normalizedFilters = {
    ...filters,
    ...(Array.isArray(filters.displayParentArchive)
      ? {displayParentArchive: filters.displayParentArchive.map(normalizeDisplayParentArchiveValue)}
      : {}),
  };
  writeSharedSettings({
    ...shared,
    filters: {
      ...(shared.filters ?? {}),
      ...normalizedFilters,
    },
  });
};

// saveApolloSharedFilterList フィルタ ID と選択値を共有フィルタとして保存。空配列は明示的な「選択なし」として保存
export const saveApolloSharedFilterList = (filterId: string, values: string[]) => {
  saveApolloSharedFilters({
    [filterId]: values.map((value) => filterId === "displayParentArchive" ? normalizeDisplayParentArchiveValue(value) : value),
  });
};

// getApolloFixedColumnId 画面上の列名を保存用の固定列 ID に変換
export const getApolloFixedColumnId = (columnName: string, pageId?: ApolloPageId) =>
  (pageId ? PAGE_FIXED_COLUMN_ID_MAP[pageId]?.[columnName] : undefined) ?? FIXED_COLUMN_ID_MAP[columnName] ?? columnName;

// getApolloDynamicColumnId CV ポイントなどの動的列を保存用 ID に変換
export const getApolloDynamicColumnId = (
  metricColumnName: string,
  setPointId: number | string,
  deleted = false,
) => {
  const metricType = DYNAMIC_METRIC_ID_MAP[metricColumnName] ?? metricColumnName;
  return `${metricType}:${deleted ? "pointDeleted" : "point"}:${setPointId}`;
};

/**
 * 画面の列名を localStorage 用の論理 ID に変換する。
 * - 固定列は画面別のマッピングを使う
 * - 動的列は generateColumns 側で apolloColumnId を埋めておく
 */
const getColumnStorageId = (column: MUIDataTableColumnDef, pageId?: ApolloPageId): string | null => {
  if (typeof column === "string") return getApolloFixedColumnId(column, pageId);
  const options = column.options as any;
  if (options?.apolloColumnId) return options.apolloColumnId;
  if (!column.name) return null;
  return getApolloFixedColumnId(column.name, pageId);
};

// isCommonColumn 画面横断で共有する共通列かどうかを判定
const isCommonColumn = (columnId: string) => Object.prototype.hasOwnProperty.call(COMMON_COLUMN_DEFAULTS, columnId);
// isHomeStatusColumn ホーム画面のステータス列かどうかを判定
const isHomeStatusColumn = (columnId: string, pageId: ApolloPageId) =>
  pageId === APOLLO_PAGE_ID.HOME && columnId === "status";
// isCommonColumnForPage 画面ごとの例外を加味して共通列かどうかを判定
const isCommonColumnForPage = (columnId: string, pageId: ApolloPageId) =>
  isCommonColumn(columnId) && !isHomeStatusColumn(columnId, pageId);
// isDynamicColumn 動的列 ID かどうかを判定
const isDynamicColumn = (columnId: string) => columnId.split(":").length === 3;
// isSharedFilter 画面横断共有フィルタかどうかを判定
const isSharedFilter = (filterId: string) => Object.prototype.hasOwnProperty.call(SHARED_FILTER_DEFAULTS, filterId);
// isPageSpecificFilter 画面固有保存にする例外フィルタかどうかを判定
const isPageSpecificFilter = (filterId: string, pageId: ApolloPageId) =>
  pageId === APOLLO_PAGE_ID.HOME && filterId === "status";
// hasStoredFilter フィルタが保存済みかどうかを判定。空配列は明示的な「選択なし」として保存済みに含める。
const hasStoredFilter = (filters: SharedFilters, filterId: string) =>
  Object.prototype.hasOwnProperty.call(filters, filterId) &&
  Array.isArray(filters[filterId]);

const COLUMN_VIEW_CHANGE_ACTIONS = new Set(["columnViewChange", "viewColumnsChange"]);
const PAGE_CHANGE_ACTIONS = new Set(["changePage"]);
const ROWS_PER_PAGE_CHANGE_ACTIONS = new Set(["changeRowsPerPage"]);
// 画面ごとに値体系が異なるフィルタは、行データ0件で候補を作れない場合に保存値をそのまま表示しない。
// 例: campaignType は MS では数値、Zeus では文字列のため、別画面の保存値が誤ったチップとして表示される。
const HIDE_WHEN_NO_FILTER_OPTIONS_IDS = new Set(["campaignType"]);
// RENDERED_FILTER_VALUE_IDS 画面ごとに実値が異なるため表示値で共有するフィルタ ID
const RENDERED_FILTER_VALUE_IDS = new Set([
  "status",
  "displayParentArchive",
  "campaignName",
  "billingType",
  "osType",
  "placementSettingId",
  "bidCtgry",
  "adType",
  "cost",
  "imp",
  "click",
  "ctr",
  "cpc",
  "cpm",
  "cvTotal",
  "lastClickCvTotal",
  "assistCvTotal",
  "cvrTotal",
  "lastClickCvrTotal",
  "assistCvrTotal",
  "cpaTotal",
  "lastClickCpaTotal",
  "assistCpaTotal",
]);

// toFilterKey フィルタ値を比較 / 保存しやすい文字列キーへ変換
const toFilterKey = (value: unknown) => String(value);

// normalizeDisplayParentArchiveValue 親アーカイブ系フィルタ値を archive / notArchive に正規化
const normalizeDisplayParentArchiveValue = (value: unknown) => {
  const key = toFilterKey(value);
  if (key === "archive" || key === "notArchive") return key;
  if (key.includes("アーカイブ以外")) return "notArchive";
  if (key.includes("アーカイブ")) return "archive";
  return key;
};

// displayParentArchive の保存用キーから画面表示用ラベルへ戻す Map
const DISPLAY_PARENT_ARCHIVE_FILTER_LABELS: {[key: string]: string} = {
  archive: "親キャンペーン・親広告グループのステータスがアーカイブ",
  notArchive: "親キャンペーン・親広告グループのステータスがアーカイブ以外",
};

// displayParentArchive 未保存時の初期選択値
const DEFAULT_DISPLAY_PARENT_ARCHIVE_FILTER_VALUES = ["notArchive"];

// getDisplayParentArchiveVisibleValues displayParentArchive の保存値を、現在画面で使える表示値に復元
const getDisplayParentArchiveVisibleValues = (
  values: unknown[],
  fallbackValues: unknown[] = [],
): string[] => {
  const fallbackValueByKey = fallbackValues.reduce<{[key: string]: string}>((acc, fallbackValue) => {
    acc[normalizeDisplayParentArchiveValue(fallbackValue)] = toFilterKey(fallbackValue);
    return acc;
  }, {});

  return values.map((value) => {
    const storageKey = normalizeDisplayParentArchiveValue(value);
    return fallbackValueByKey[storageKey] ?? DISPLAY_PARENT_ARCHIVE_FILTER_LABELS[storageKey] ?? toFilterKey(value);
  });
};

// getDefaultDisplayParentArchiveFilterList displayParentArchive 未保存時に適用する初期 filterList を返却
const getDefaultDisplayParentArchiveFilterList = (fallbackValues: unknown[] = []) =>
  getDisplayParentArchiveVisibleValues(DEFAULT_DISPLAY_PARENT_ARCHIVE_FILTER_VALUES, fallbackValues);

// toStorageFilterKey フィルタ表示値 / 生値を localStorage 保存用キーに変換
const toStorageFilterKey = (filterId: string, value: unknown, filterValueMap?: ApolloFilterValueMap | null) => {
  if (filterId === "displayParentArchive") {
    const valueKey = toFilterKey(value);
    const mappedValue = filterValueMap?.rawToStorage[valueKey];
    return mappedValue === undefined ? normalizeDisplayParentArchiveValue(value) : toFilterKey(mappedValue);
  }
  const valueKey = toFilterKey(value);
  const mappedValue = filterValueMap?.rawToStorage[valueKey];
  return mappedValue === undefined ? valueKey : toFilterKey(mappedValue);
};

// getValueByPath ドット区切りパスから行データの値を取得
const getValueByPath = (row: any, path: string) => {
  if (!row || !path) return undefined;
  return path.split(".").reduce((acc, key) => acc?.[key], row);
};

// フィルタ表示値と保存値を相互変換するための Map
type ApolloFilterValueMap = {
  availableStorageKeys: Set<string>;
  storageToRaw: {[key: string]: unknown};
  rawToStorage: {[key: string]: unknown};
};

// ApolloFilterValueMap を列 options に埋め込めるよう配列化した形式
type ApolloSerializableFilterValueMap = {
  availableStorageKeys: string[];
  storageToRaw: {[key: string]: unknown};
  rawToStorage: {[key: string]: unknown};
};

// getFilterValueMap 現在の表示データからフィルタ値の保存キー / 表示値変換 Map を生成
const getFilterValueMap = (
  column: MUIDataTableColumnDef,
  columnId: string,
  data?: any[],
) : ApolloFilterValueMap | null => {
  if (typeof column === "string" || !data?.length) return null;
  const columnName = column.name;
  if (!columnName) return null;
  const useRenderedFilterValue = RENDERED_FILTER_VALUE_IDS.has(columnId);
  const renderValue =
    (column.options as any)?.filterOptions?.renderValue ??
    (column.options as any)?.customFilterListOptions?.render ??
    (useRenderedFilterValue ? (column.options as any)?.customBodyRender : undefined);
  const availableStorageKeys = new Set<string>();
  const storageToRaw: {[key: string]: unknown} = {};
  const rawToStorage: {[key: string]: unknown} = {};

  data.forEach((row) => {
    const value = getValueByPath(row, columnName);
    let storageValue = value;
    let renderedValue: unknown = undefined;
    if (typeof renderValue === "function") {
      try {
        renderedValue = renderValue(value);
        if (renderedValue !== undefined && renderedValue !== null && renderedValue !== "") {
          storageValue = renderedValue;
        }
      } catch {
        // renderValue が扱えない値は生値のまま利用する。
      }
    }
    if (columnId === "displayParentArchive") {
      storageValue = normalizeDisplayParentArchiveValue(storageValue);
    }
    const storageKey = toFilterKey(storageValue);
    const rawKey = toFilterKey(value);
    const filterListValue = useRenderedFilterValue
      && renderedValue !== undefined
      && renderedValue !== null
      && renderedValue !== ""
      ? renderedValue
      : value;
    availableStorageKeys.add(storageKey);
    storageToRaw[storageKey] = filterListValue;
    rawToStorage[rawKey] = storageValue;
    rawToStorage[storageKey] = storageValue;
    if (renderedValue !== undefined && renderedValue !== null && renderedValue !== "") {
      rawToStorage[toFilterKey(renderedValue)] = storageValue;
    }
  });

  return availableStorageKeys.size ? {availableStorageKeys, storageToRaw, rawToStorage} : null;
};

// getVisibleFilterValues 保存済みフィルタのうち、現在の表示データで選択可能な値だけを復元
// 共有フィルタは画面横断で使うため、遷移先に存在しない値はここで落とす。
const getVisibleFilterValues = (
  values: unknown[] | null | undefined,
  filterValueMap: ApolloFilterValueMap | null,
  filterId: string,
  fallbackValues: unknown[] = [],
) => {
  if (!Array.isArray(values)) return [];
  if (!filterValueMap) {
    if (HIDE_WHEN_NO_FILTER_OPTIONS_IDS.has(filterId)) return [];
    return filterId === "displayParentArchive"
      ? getDisplayParentArchiveVisibleValues(values, fallbackValues)
      : values;
  }
  return values
    .filter((value) => filterValueMap.availableStorageKeys.has(toStorageFilterKey(filterId, value, filterValueMap)))
    .map((value) => {
      const storageKey = toStorageFilterKey(filterId, value, filterValueMap);
      return filterValueMap.storageToRaw[storageKey] ?? value;
    });
};

// getRestoredFilterListValues 保存済みフィルタ値を DataTable の filterList に戻す
// ページ固有フィルタは同じ画面に戻す値なので、現在の表示データに無くても除外しない。
const getRestoredFilterListValues = (
  values: unknown[] | null | undefined,
  filterValueMap: ApolloFilterValueMap | null,
  filterId: string,
  fallbackValues: unknown[] = [],
) => {
  if (!Array.isArray(values)) return [];
  if (!filterValueMap) {
    if (HIDE_WHEN_NO_FILTER_OPTIONS_IDS.has(filterId)) return [];
    return filterId === "displayParentArchive"
      ? getDisplayParentArchiveVisibleValues(values, fallbackValues)
      : values;
  }
  return values.map((value) => {
    const storageKey = toStorageFilterKey(filterId, value, filterValueMap);
    return filterValueMap.storageToRaw[storageKey] ?? value;
  });
};

// mergeVisibleFilterValues 画面で変更されたフィルタ値を保存用の値に変換する
const mergeVisibleFilterValues = (
  currentValues: unknown[] | null | undefined,
  nextVisibleValues: unknown[],
  filterValueMap: ApolloFilterValueMap | null,
  filterId: string,
  preserveHiddenValues = true,
) => {
  if (!filterValueMap) return nextVisibleValues;
  const current = Array.isArray(currentValues) ? currentValues : [];
  // 変更していない列は候補外の保存値を残すが、変更された列では現在画面の選択値で置き換える。
  // campaignType のように MS / Zeus で同じ保存IDでも値体系が違うフィルタが混在することを防ぐ。
  const hiddenValues = preserveHiddenValues
    ? current.filter((value) => !filterValueMap.availableStorageKeys.has(toStorageFilterKey(filterId, value, filterValueMap)))
    : [];
  const nextStorageValues = nextVisibleValues.map((value) => filterValueMap.rawToStorage[toFilterKey(value)] ?? value);
  const mergedValues = [...hiddenValues, ...nextStorageValues];
  const seen = new Set<string>();
  return mergedValues.filter((value) => {
    const key = toStorageFilterKey(filterId, value, filterValueMap);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

// shouldApplyFilterChange 対象列のフィルタ変更を保存へ反映するか判定
// mui-datatables は変更していない列の filterList も一緒に返すため、変更列以外は保存済み値を優先する。
const shouldApplyFilterChange = (
  index: number,
  columnId: string,
  column: MUIDataTableColumnDef,
  changedColumnIndex?: number | null,
  changedColumn?: string | MUIDataTableColumnDef | null,
  type?: string | null,
) => {
  if (type === "reset") return true;
  if (changedColumnIndex !== undefined && changedColumnIndex !== null) return index === changedColumnIndex;
  if (!changedColumn) return false;
  if (typeof changedColumn !== "string") {
    return changedColumn === column ||
      changedColumn.name === columnId ||
      (typeof column !== "string" && changedColumn.name === column.name);
  }
  if (changedColumn === columnId) return true;
  if (typeof column === "string") return changedColumn === column;
  return changedColumn === column.name || changedColumn === column.label;
};

// readFilterValueMapFromColumn 列 options に埋め込んだフィルタ値変換 Map を読み戻す
const readFilterValueMapFromColumn = (column: MUIDataTableColumnDef): ApolloFilterValueMap | null => {
  if (typeof column === "string") return null;
  const map = (column.options as any)?.apolloFilterValueMap as ApolloSerializableFilterValueMap | undefined;
  return map?.availableStorageKeys?.length
    ? {
      availableStorageKeys: new Set<string>(map.availableStorageKeys.map(toFilterKey)),
      storageToRaw: map.storageToRaw ?? {},
      rawToStorage: map.rawToStorage ?? {},
    }
    : null;
};

// getDefaultDisplay 列 ID に応じた初期表示状態を返却
const getDefaultDisplay = (column: MUIDataTableColumnDef, columnId: string, pageId?: ApolloPageId) => {
  if (isCommonColumn(columnId)) return COMMON_COLUMN_DEFAULTS[columnId];
  if (pageId && Object.prototype.hasOwnProperty.call(PAGE_COLUMN_DEFAULT_OVERRIDES[pageId] ?? {}, columnId)) {
    return PAGE_COLUMN_DEFAULT_OVERRIDES[pageId]?.[columnId] ?? true;
  }
  if (isDynamicColumn(columnId)) {
    const [metricType, kind] = columnId.split(":");
    return kind === "point" && metricType !== "assistCvr";
  }
  if (typeof column === "string") return true;
  return normalizeDisplay(column.options?.display, true);
};

// readCommonColumns A：メイン画面共通保持項目 要件の共通列表示設定を取得
const readCommonColumns = () => {
  const stored = readJson<ApolloColumnStorage>(APOLLO_LOCAL_STORAGE_KEYS.COLUMNS_COMMON, {columns: {}});
  return {
    columns: {
      ...COMMON_COLUMN_DEFAULTS,
      ...filterColumns(stored.columns ?? {}, Object.keys(COMMON_COLUMN_DEFAULTS)),
    },
  };
};

// writeCommonColumns A：メイン画面共通保持項目 要件の共通列表示設定を保存
const writeCommonColumns = (columns: ColumnValueMap) => {
  writeJson(APOLLO_LOCAL_STORAGE_KEYS.COLUMNS_COMMON, {
    columns: {
      ...COMMON_COLUMN_DEFAULTS,
      ...filterColumns(columns, Object.keys(COMMON_COLUMN_DEFAULTS)),
    },
  });
};

// readPageSpecificColumns 画面固有列の表示 / フィルタ設定を取得
const readPageSpecificColumns = (pageId: ApolloPageId) =>
  readJson<ApolloColumnStorage>(pageSpecificKey(pageId), {columns: {}});

// writePageSpecificColumns 画面固有列の表示設定を保存
const writePageSpecificColumns = (pageId: ApolloPageId, columns: ColumnValueMap, allowedIds: string[]) => {
  const current = readPageSpecificColumns(pageId);
  writeJson(pageSpecificKey(pageId), {
    columns: filterColumns(columns, allowedIds),
    ...(current.filters ? {filters: current.filters} : {}),
  });
};

// writePageSpecificFilters 画面固有フィルタ設定を保存
const writePageSpecificFilters = (pageId: ApolloPageId, filters: SharedFilters) => {
  const current = readPageSpecificColumns(pageId);
  writeJson(pageSpecificKey(pageId), {
    columns: current.columns ?? {},
    filters: {
      ...(current.filters ?? {}),
      ...filters,
    },
  });
};

// readDynamicColumns アカウントグループ別の動的列表示設定を取得
const readDynamicColumns = (accountGroupId?: number | string | null): ApolloDynamicColumnStorage => {
  const stored = readJson<any>(sharedDynamicKey(accountGroupId), {dynamicColumns: {}});
  if (stored?.dynamicColumns && typeof stored.dynamicColumns === "object") {
    return {dynamicColumns: stored.dynamicColumns};
  }
  if (stored && typeof stored === "object") {
    return {dynamicColumns: stored};
  }
  return {dynamicColumns: {}};
};

// writeDynamicColumns アカウントグループ別の動的列表示設定を保存
const writeDynamicColumns = (
  accountGroupId: number | string | null | undefined,
  columns: ColumnValueMap,
  allowedIds: string[],
) => {
  writeJson(sharedDynamicKey(accountGroupId), {
    dynamicColumns: filterColumns(columns, allowedIds),
  });
};

// syncDynamicColumnDefaults 最新の動的列定義に存在する未保存列だけを初期値で補完する
const syncDynamicColumnDefaults = (
  columns: MUIDataTableColumnDef[],
  params: ApolloSharedColumnOptions,
) => {
  const dynamicAllowedIds: string[] = [];
  const dynamicDefaults: ColumnValueMap = {};

  columns.forEach((column) => {
    if (typeof column !== "string" && (column.options?.viewColumns === false || column.options?.display === "excluded")) return;
    const columnId = getColumnStorageId(column, params.pageId);
    if (!columnId) return;
    if (isDynamicColumn(columnId)) {
      dynamicAllowedIds.push(columnId);
      dynamicDefaults[columnId] = getDefaultDisplay(column, columnId, params.pageId);
    }
  });

  if (dynamicAllowedIds.length) {
    writeJson(sharedDynamicKey(params.accountGroupId), {
      dynamicColumns: {
        ...dynamicDefaults,
        ...readDynamicColumns(params.accountGroupId).dynamicColumns,
      },
    });
  }
};

// applyApolloSharedColumnSettings localStorage の列表示 / フィルタ設定を DataTable 列定義へ反映
export const applyApolloSharedColumnSettings = (
  columns: MUIDataTableColumnDef[],
  params: ApolloSharedColumnOptions,
) => {
  // 画面表示時は
  // 1. A の共通フィルタ
  // 2. B の画面固有フィルタ
  // 3. A の共通列 / 動的列 / 画面固有列
  // を順に反映する
  const common = readCommonColumns().columns;
  const pageSpecificStorage = readPageSpecificColumns(params.pageId);
  const pageSpecific = pageSpecificStorage.columns;
  const pageSpecificFilters: SharedFilters = pageSpecificStorage.filters ?? {};
  const dynamic = readDynamicColumns(params.accountGroupId).dynamicColumns;
  const sharedFilters: SharedFilters = readSharedSettings().filters ?? {};
  const pageFilters: SharedFilters = readPageState(params.pageId).filters ?? {};

  return columns.map((column) => {
    if (typeof column === "string") return column;
    if (column.options?.filter === false) {
      column = {
        ...column,
        options: {
          ...column.options,
          // 動的列の増減時に mui-datatables が以前のインデックスの
          // filterList を引き継がないよう、明示的に空配列を設定する。
          filterList: [],
        },
      };
    }
    if (column.options?.display === "excluded") return column;
    if (column.options?.viewColumns === false && column.options?.filter !== true) return column;

    const columnId = getColumnStorageId(column, params.pageId);
    if (!columnId) return column;
    const canApplyFilter = column.options?.filter !== false;

    let display = getDefaultDisplay(column, columnId, params.pageId);
    if (isCommonColumnForPage(columnId, params.pageId)) {
      display = common[columnId];
    } else if (isDynamicColumn(columnId)) {
      display = Object.prototype.hasOwnProperty.call(dynamic, columnId) ? dynamic[columnId] : display;
    } else if (Object.prototype.hasOwnProperty.call(pageSpecific, columnId)) {
      display = pageSpecific[columnId];
    }

    const hasPageSpecificFilter = canApplyFilter &&
      isPageSpecificFilter(columnId, params.pageId) &&
      hasStoredFilter(pageSpecificFilters, columnId);
    const hasSharedFilter = canApplyFilter &&
      !hasPageSpecificFilter &&
      !isPageSpecificFilter(columnId, params.pageId) &&
      isSharedFilter(columnId) &&
      hasStoredFilter(sharedFilters, columnId);
    const hasPageFilter = canApplyFilter && hasStoredFilter(pageFilters, columnId);
    const filterValueMap = getFilterValueMap(column, columnId, params.data);
    const fallbackFilterList = Array.isArray((column.options as any)?.filterList)
      ? (column.options as any).filterList
      : [];
    const useDefaultDisplayParentArchiveFilter = columnId === "displayParentArchive" &&
      !hasPageSpecificFilter &&
      !hasSharedFilter &&
      !hasPageFilter;
    const filterList = hasPageSpecificFilter
      ? {filterList: getVisibleFilterValues(pageSpecificFilters[columnId], filterValueMap, columnId, fallbackFilterList)}
      : hasSharedFilter
      ? {filterList: getVisibleFilterValues(sharedFilters[columnId], filterValueMap, columnId, fallbackFilterList)}
      : hasPageFilter
        // ページ固有フィルタは同一画面用の保存値なので、候補に無い値も初期表示へ戻す。
        // 例: Zeus プレースメント「-」は保存値として残っていても、候補生成のタイミング次第で一時的に候補外になる。
        ? {filterList: getRestoredFilterListValues(pageFilters[columnId], filterValueMap, columnId, fallbackFilterList)}
        : useDefaultDisplayParentArchiveFilter
          ? {filterList: getDefaultDisplayParentArchiveFilterList(fallbackFilterList)}
        : {};

    return {
      ...column,
      options: {
        ...column.options,
        ...filterList,
        display,
        apolloColumnId: columnId,
        ...(filterValueMap ? {
          apolloFilterValueMap: {
            availableStorageKeys: Array.from(filterValueMap.availableStorageKeys),
            storageToRaw: filterValueMap.storageToRaw,
            rawToStorage: filterValueMap.rawToStorage,
          },
        } : {}),
      } as any,
    };
  });
};

// saveColumnStateFromTableState DataTable の列表示状態を A / B / 動的列の保存先へ振り分けて保存
const saveColumnStateFromTableState = (
  columns: MUIDataTableColumnDef[],
  tableStateColumns: any[],
  params: ApolloSharedColumnOptions,
) => {
  if (!tableStateColumns?.length) return;

  const commonUpdates: ColumnValueMap = {};
  const pageUpdates: ColumnValueMap = {};
  const dynamicUpdates: ColumnValueMap = {};
  const pageAllowedIds: string[] = [];
  const dynamicAllowedIds: string[] = [];

  columns.forEach((column, index) => {
    if (typeof column !== "string" && (column.options?.viewColumns === false || column.options?.display === "excluded")) return;
    const columnId = getColumnStorageId(column, params.pageId);
    if (!columnId) return;
    const visible = normalizeDisplay(tableStateColumns[index]?.display, getDefaultDisplay(column, columnId, params.pageId));

    if (isCommonColumnForPage(columnId, params.pageId)) {
      commonUpdates[columnId] = visible;
    } else if (isDynamicColumn(columnId)) {
      dynamicUpdates[columnId] = visible;
      dynamicAllowedIds.push(columnId);
    } else {
      pageUpdates[columnId] = visible;
      pageAllowedIds.push(columnId);
    }
  });

  if (Object.keys(commonUpdates).length) {
    writeCommonColumns({
      ...readCommonColumns().columns,
      ...commonUpdates,
    });
  }

  if (Object.keys(pageUpdates).length) {
    writePageSpecificColumns(params.pageId, {
      ...readPageSpecificColumns(params.pageId).columns,
      ...pageUpdates,
    }, pageAllowedIds);
  }

  if (Object.keys(dynamicUpdates).length) {
    writeDynamicColumns(params.accountGroupId, {
      ...readDynamicColumns(params.accountGroupId).dynamicColumns,
      ...dynamicUpdates,
    }, dynamicAllowedIds);
  }
};

// saveFilterStateFromTableState DataTable のフィルタ状態を A / B の保存先へ振り分けて保存
const saveFilterStateFromTableState = (
  columns: MUIDataTableColumnDef[],
  filterList: string[][],
  pageId: ApolloPageId,
  changedColumnIndex?: number | null,
  changedColumn?: string | MUIDataTableColumnDef | null,
  type?: string | null,
) => {
  if (!filterList?.length) return filterList ?? [];

  // A 対象のフィルタと B 対象のフィルタで保存先を分ける
  const sharedUpdates: SharedFilters = {};
  const pageUpdates: SharedFilters = {};
  const pageSpecificUpdates: SharedFilters = {};
  // 保存に使う値とは別に、DataTable の列定義へ戻す filterList を作る。
  // 変更していない列は MUI から渡された一時値ではなく、localStorage の保存値を戻す。
  const restoredFilterList: string[][] = columns.map((_, index) => [...(filterList[index] ?? [])]);

  columns.forEach((column, index) => {
    if (typeof column !== "string" && column.options?.filter === false) {
      restoredFilterList[index] = [];
      return;
    }
    const columnId = getColumnStorageId(column, pageId);
    if (!columnId) return;
    const filterValues = filterList[index] ?? [];
    const filterValueMap = readFilterValueMapFromColumn(column);
    const fallbackFilterList = typeof column !== "string" && Array.isArray((column.options as any)?.filterList)
      ? (column.options as any).filterList
      : [];
    const applyVisibleChange = shouldApplyFilterChange(index, columnId, column, changedColumnIndex, changedColumn, type);
    if (isPageSpecificFilter(columnId, pageId)) {
      const currentValues = readPageSpecificColumns(pageId).filters?.[columnId];
      if (!applyVisibleChange && Array.isArray(currentValues)) {
        restoredFilterList[index] = getRestoredFilterListValues(currentValues, filterValueMap, columnId, fallbackFilterList) as string[];
        return;
      }
      const mergedValues = mergeVisibleFilterValues(currentValues, filterValues, filterValueMap, columnId);
      pageSpecificUpdates[columnId] = mergedValues as string[];
      restoredFilterList[index] = getRestoredFilterListValues(mergedValues, filterValueMap, columnId, fallbackFilterList) as string[];
      return;
    }
    if (isSharedFilter(columnId)) {
      const currentValues = readSharedSettings().filters?.[columnId];
      if (!applyVisibleChange && Array.isArray(currentValues)) {
        restoredFilterList[index] = getVisibleFilterValues(currentValues, filterValueMap, columnId, fallbackFilterList) as string[];
        return;
      }
      const mergedValues = mergeVisibleFilterValues(currentValues, filterValues, filterValueMap, columnId);
      sharedUpdates[columnId] = mergedValues as string[];
      restoredFilterList[index] = getVisibleFilterValues(mergedValues, filterValueMap, columnId, fallbackFilterList) as string[];
      return;
    }
    const currentValues = readPageState(pageId).filters?.[columnId];
    if (!applyVisibleChange && Array.isArray(currentValues)) {
      restoredFilterList[index] = getRestoredFilterListValues(currentValues, filterValueMap, columnId, fallbackFilterList) as string[];
      return;
    }
    const mergedValues = mergeVisibleFilterValues(currentValues, filterValues, filterValueMap, columnId);
    pageUpdates[columnId] = mergedValues as string[];
    restoredFilterList[index] = getRestoredFilterListValues(mergedValues, filterValueMap, columnId, fallbackFilterList) as string[];
  });

  if (Object.keys(sharedUpdates).length) {
    saveApolloSharedFilters(sharedUpdates);
  }

  if (Object.keys(pageSpecificUpdates).length) {
    writePageSpecificFilters(pageId, pageSpecificUpdates);
  }

  if (Object.keys(pageUpdates).length) {
    savePageState(pageId, {
      filters: {
        ...(readPageState(pageId).filters ?? {}),
        ...pageUpdates,
      },
    });
  }
  return restoredFilterList;
};

// syncColumnFilterLists DataTable 再構築時に現在のフィルタを維持できるよう列定義へ反映
const syncColumnFilterLists = (
  columns: MUIDataTableColumnDef[],
  filterList: string[][],
) => {
  columns.forEach((column, index) => {
    if (typeof column === "string") return;
    column.options = {
      ...column.options,
      filterList: [...(filterList[index] ?? [])],
    };
  });
};

// syncColumnDisplays View Columns 変更直後の表示状態を列定義へ反映
const syncColumnDisplays = (
  columns: MUIDataTableColumnDef[],
  tableStateColumns: any[],
) => {
  if (!Array.isArray(tableStateColumns)) return;
  columns.forEach((column, index) => {
    if (typeof column === "string") return;
    if (column.options?.viewColumns === false || column.options?.display === "excluded") return;
    const display = tableStateColumns[index]?.display;
    if (display === undefined) return;
    column.options = {
      ...column.options,
      display,
    };
  });
};

// syncMuiDataTableStorage 既存 DataTable storage に Apollo 側の表示 / フィルタ / ページ状態を同期
const syncMuiDataTableStorage = (
  storageKey: string | undefined,
  columns: MUIDataTableColumnDef[],
  rowsPerPage: number,
  pageId: ApolloPageId,
) => {
  if (!storageKey) return;
  const stored = readJson<any | null>(storageKey, null);
  if (!stored || typeof stored !== "object") return;

  let changed = false;

  if (Array.isArray(stored.columns)) {
    stored.columns = stored.columns.map((storedColumn: any, index: number) => {
      const sourceColumn = columns[index];
      if (typeof sourceColumn === "string" || !sourceColumn?.options) return storedColumn;
      if (sourceColumn.options.viewColumns === false || sourceColumn.options.display === "excluded") return storedColumn;

      const display = sourceColumn.options.display;
      if (typeof display === "boolean" && storedColumn?.display !== display) {
        changed = true;
        return {
          ...storedColumn,
          display,
        };
      }
      return storedColumn;
    });
  }

  if (stored.rowsPerPage !== rowsPerPage) {
    stored.rowsPerPage = rowsPerPage;
    changed = true;
  }

  const pageState = readPageState(pageId);
  const pageSpecificFilters: SharedFilters = readPageSpecificColumns(pageId).filters ?? {};
  if (typeof pageState.jumpToPage === "number" && stored.page !== pageState.jumpToPage) {
    stored.page = pageState.jumpToPage;
    changed = true;
  }

  if (pageState.sort?.name && pageState.sort?.direction) {
    const nextSortOrder = {
      name: pageState.sort.name,
      direction: pageState.sort.direction,
    };
    if (
      stored.sortOrder?.name !== nextSortOrder.name ||
      stored.sortOrder?.direction !== nextSortOrder.direction
    ) {
      stored.sortOrder = nextSortOrder;
      changed = true;
    }
  }

  if (Array.isArray(stored.filterList)) {
    const sharedFilters: SharedFilters = readSharedSettings().filters ?? {};
    const pageFilters: SharedFilters = pageState.filters ?? {};
    // 動的 CV 列は再描画時に増減する可能性がある。
    // 以前の配列を再利用すると、不要になった列インデックスにフィルタが残留する。
    const hasSameColumnLayout = Array.isArray(stored.columns) &&
      stored.columns.length === columns.length &&
      stored.columns.every((storedColumn: any, index: number) => {
        const column = columns[index];
        const columnName = typeof column === "string" ? column : column.name;
        return storedColumn?.name === columnName;
      });
    const nextFilterList = columns.map((column, index) => {
      if (hasSameColumnLayout && Array.isArray(stored.filterList[index])) {
        return [...stored.filterList[index]];
      }
      if (typeof column !== "string" && Array.isArray((column.options as any)?.filterList)) {
        return [...(column.options as any).filterList];
      }
      return [];
    });
    columns.forEach((column, index) => {
      if (typeof column !== "string" && column.options?.filter === false) {
        if (nextFilterList[index]?.length) {
          nextFilterList[index] = [];
          changed = true;
        }
        return;
      }
      const columnId = getColumnStorageId(column, pageId);
      if (!columnId) return;
      if (
        isPageSpecificFilter(columnId, pageId) &&
        hasStoredFilter(pageSpecificFilters, columnId)
      ) {
        const value = pageSpecificFilters[columnId];
        const filterValueMap = readFilterValueMapFromColumn(column);
        const fallbackFilterList = Array.isArray((column as any)?.options?.filterList)
          ? (column as any).options.filterList
          : [];
        nextFilterList[index] = getVisibleFilterValues(value, filterValueMap, columnId, fallbackFilterList);
        changed = true;
        return;
      }
      if (
        !isPageSpecificFilter(columnId, pageId) &&
        isSharedFilter(columnId) &&
        hasStoredFilter(sharedFilters, columnId)
      ) {
        const value = sharedFilters[columnId];
        const filterValueMap = readFilterValueMapFromColumn(column);
        const fallbackFilterList = Array.isArray((column as any)?.options?.filterList)
          ? (column as any).options.filterList
          : [];
        nextFilterList[index] = getVisibleFilterValues(value, filterValueMap, columnId, fallbackFilterList);
        changed = true;
        return;
      }
      if (hasStoredFilter(pageFilters, columnId)) {
        const value = pageFilters[columnId];
        const filterValueMap = readFilterValueMapFromColumn(column);
        const fallbackFilterList = Array.isArray((column as any)?.options?.filterList)
          ? (column as any).options.filterList
          : [];
        // MUIDataTable 自身の storageKey にも、ページ固有フィルタは保存値をそのまま反映する。
        // ここで候補外の値を落とすと、再描画時に画面上の選択だけ外れてしまう。
        nextFilterList[index] = getRestoredFilterListValues(value, filterValueMap, columnId, fallbackFilterList);
        changed = true;
        return;
      }
      if (columnId === "status") {
        const fallbackFilterList = Array.isArray((column as any)?.options?.filterList)
          ? (column as any).options.filterList
          : [];
        if (fallbackFilterList.length) {
          nextFilterList[index] = fallbackFilterList;
          changed = true;
        }
        return;
      }
      if (columnId === "displayParentArchive") {
        const fallbackFilterList = Array.isArray((column as any)?.options?.filterList)
          ? (column as any).options.filterList
          : [];
        nextFilterList[index] = getDefaultDisplayParentArchiveFilterList(fallbackFilterList);
        changed = true;
      }
    });
    if (JSON.stringify(stored.filterList) !== JSON.stringify(nextFilterList)) {
      stored.filterList = nextFilterList;
      changed = true;
    }
  }

  if (changed) writeJson(storageKey, stored);
};

// createApolloSharedTableOptions Apollo の localStorage 復元 / 保存処理を MUIDataTable options に付与
export const createApolloSharedTableOptions = (
  options: ApolloTableOptionsWithStorage,
  params: ApolloSharedTableOptionsParams,
): ApolloTableOptionsWithStorage => {
  const defaultPageSize = params.defaultPageSize ?? options.rowsPerPage ?? DEFAULT_PAGE_SIZE;
  const rowsPerPage = SHARED_PAGE_SIZE_TARGETS.has(params.pageId)
    ? getApolloSharedPageSize(defaultPageSize)
    : getPageSizeSetting(params.pageId, defaultPageSize);
  const pageState = readPageState(params.pageId);
  const sortOrder: MUISortOptions | undefined = pageState.sort?.name && pageState.sort?.direction
    ? {
      name: pageState.sort.name,
      direction: pageState.sort.direction as MUISortOptions["direction"],
    }
    : options.sortOrder;
  const page = typeof pageState.jumpToPage === "number" ? pageState.jumpToPage : options.page;

  syncDynamicColumnDefaults(params.columns, params);
  syncMuiDataTableStorage(options.storageKey, params.columns, rowsPerPage, params.pageId);

  return {
    ...options,
    rowsPerPage,
    sortOrder,
    page,
    onChangeRowsPerPage: (numberOfRows: number) => {
      if (SHARED_PAGE_SIZE_TARGETS.has(params.pageId)) {
        saveApolloSharedPageSize(numberOfRows);
      } else {
        savePageSizeSetting(params.pageId, numberOfRows);
      }
      options.onChangeRowsPerPage?.(numberOfRows);
    },
    onChangePage: (currentPage: number) => {
      savePageState(params.pageId, {jumpToPage: currentPage});
      options.onChangePage?.(currentPage);
    },
    onFilterChange: (changedColumn, filterList, type, changedColumnIndex, displayData) => {
      // onFilterChange 後の列定義にも保存値を反映し、次の propsUpdate で選択状態が落ちないようにする。
      const restoredFilterList = saveFilterStateFromTableState(
        params.columns,
        filterList as string[][],
        params.pageId,
        changedColumnIndex as number | null,
        changedColumn,
        type,
      );
      syncColumnFilterLists(params.columns, restoredFilterList as string[][]);
      options.onFilterChange?.(changedColumn, filterList, type, changedColumnIndex, displayData);
    },
    onTableChange: (action: string, tableState: any) => {
      if (ROWS_PER_PAGE_CHANGE_ACTIONS.has(action) && typeof tableState?.rowsPerPage === "number") {
        if (SHARED_PAGE_SIZE_TARGETS.has(params.pageId)) {
          saveApolloSharedPageSize(tableState.rowsPerPage);
        } else {
          savePageSizeSetting(params.pageId, tableState.rowsPerPage);
        }
      }
      // propsUpdate やデータ再取得時にも tableState.page が 0 で返ることがある。
      // JumpToPage 保存値は、実際にページ変更した action の時だけ更新する。
      if (PAGE_CHANGE_ACTIONS.has(action) && typeof tableState?.page === "number") {
        savePageState(params.pageId, {jumpToPage: tableState.page});
      }
      if (tableState?.sortOrder?.name && tableState?.sortOrder?.direction) {
        savePageState(params.pageId, {
          sort: {
            name: tableState.sortOrder.name,
            direction: tableState.sortOrder.direction,
          },
        });
      }
      if (COLUMN_VIEW_CHANGE_ACTIONS.has(action)) {
        saveColumnStateFromTableState(params.columns, tableState?.columns ?? [], params);
        syncColumnDisplays(params.columns, tableState?.columns ?? []);
      }
      // フィルタは onFilterChange で保存する。
      // Rows per page 変更や再描画途中の一時的な filterList で保存値を上書きしない。
      options.onTableChange?.(action, tableState);
    },
  };
};

// clearApolloLocalStorageSettings Apollo の localStorage 設定をすべて削除
export const clearApolloLocalStorageSettings = () => {
  if (!isBrowserStorageAvailable()) return;
  const keysToRemove: string[] = [
    APOLLO_LOCAL_STORAGE_KEYS.SHARED,
    APOLLO_LOCAL_STORAGE_KEYS.PAGE_SETTINGS,
    APOLLO_LOCAL_STORAGE_KEYS.COLUMNS_COMMON,
  ];

  for (let i = 0; i < window.localStorage.length; i++) {
    const key = window.localStorage.key(i);
    if (!key) continue;
    if (
      key.startsWith(APOLLO_LOCAL_STORAGE_KEYS.COLUMNS_SHARED_PREFIX) ||
      key.startsWith(APOLLO_LOCAL_STORAGE_KEYS.COLUMNS_PAGE_SPECIFIC_PREFIX)
    ) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach((key) => window.localStorage.removeItem(key));
};
